import useSendbirdStateContext from "@sendbird/uikit-react/useSendbirdStateContext";
import { useMemo } from "react";
import { useInternalOpsChannels } from "@/lib/api/internal-ops-messaging";
import { useGetMyMentorships } from "@/lib/api/mentorships";
import { useCurrentUser, useGetUserSupervisorAndChannel } from "@/lib/api/user";
import {
  devOverridesEnabled,
  useDevOverridesStore,
} from "@/lib/hooks/admin/use-dev-overrides";
import { useChannelsList } from "@/lib/hooks/conversations/use-channels-list";
import { isMentorIdentity } from "@/lib/utils/system-roles";
import { useDismissedKindsByMentorship } from "../action/context/use-action-suggestion-dismissals";
import {
  buildNoticeView,
  type NoticeView,
  type NoticeViewResult,
} from "./notice-view";
import { getNoticeWorkspaceBehavior } from "./notice-workspace";
import type { MentorWorkspaceId } from "./workspace";

export type { NoticeView };
export type UseNoticeConversationsResult = NoticeViewResult;

export function useNoticeConversations(
  workspace: MentorWorkspaceId,
): UseNoticeConversationsResult {
  const store = useSendbirdStateContext();
  const currentUserId = store.config.userId;
  const behavior = getNoticeWorkspaceBehavior(workspace);
  const rawQaScenario = useDevOverridesStore(
    (state) => state.d7TriggerQaScenario,
  );
  const qaScenario = devOverridesEnabled ? rawQaScenario : null;
  const dismissedByMentorship = useDismissedKindsByMentorship();

  const {
    channels,
    loading: channelsLoading,
    error: channelsError,
  } = useChannelsList({
    customTypesFilter: behavior.customTypesFilter,
    enableNotifications: false,
  });

  const { data: mentorships } = useGetMyMentorships({
    enabled: behavior.fetchesMentorshipSignals,
  });

  const {
    data: internalChannels,
    isLoading: internalLoading,
    error: internalError,
  } = useInternalOpsChannels(behavior.fetchesInternalOps);
  const { data: currentUser } = useCurrentUser();
  const isCurrentUserMentor = isMentorIdentity(currentUser?.systemRoles ?? []);
  const { data: supervisorData } = useGetUserSupervisorAndChannel(
    currentUser?.id ?? "",
    behavior.fetchesInternalOps && !!currentUser?.id && isCurrentUserMentor,
  );
  const activeSupervisorId = supervisorData?.supervisor?.id ?? null;

  return useMemo(
    () =>
      buildNoticeView({
        workspace,
        channels,
        mentorships: mentorships ?? [],
        internalOpsChannels: internalChannels ?? [],
        viewerContext: {
          currentUserId,
          isCurrentUserMentor,
          activeSupervisorId,
        },
        channelsLoading,
        channelsError,
        internalLoading,
        internalError: internalError?.message ?? null,
        qaScenario,
        dismissedByMentorship,
      }),
    [
      workspace,
      channels,
      mentorships,
      internalChannels,
      currentUserId,
      isCurrentUserMentor,
      activeSupervisorId,
      channelsLoading,
      channelsError,
      internalLoading,
      internalError,
      qaScenario,
      dismissedByMentorship,
    ],
  );
}
